import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AdminHeader, timeAgo } from "./adminShared";
import { Loader2, Eye, Heart, Share2, MousePointerClick, Search, SlidersHorizontal, CalendarPlus, Activity, Trash2 } from "lucide-react";

const ICONS = {
  race_view: Eye, save_race: Heart, remove_save: Heart, share: Share2,
  register_click: MousePointerClick, search: Search, filter: SlidersHorizontal, calendar_add: CalendarPlus,
};
const LABELS = {
  race_view: "Race Views", save_race: "Saves", remove_save: "Save Removed", share: "Shares",
  register_click: "Registration Clicks", search: "Searches", filter: "Filter Uses", calendar_add: "Calendar Adds",
};

export default function AdminReports() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/analytics").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const hide = async (id) => {
    try { await api.delete(`/admin/analytics/recent/${id}`); load(); }
    catch { toast.error("Could not remove event"); }
  };
  const clearAll = async () => {
    if (!window.confirm("Clear all recent events?\n\nThis only hides them from this list. Your aggregate totals above stay unchanged.")) return;
    try { const { data } = await api.post("/admin/analytics/recent/clear"); toast.success(`Cleared ${data.cleared} event(s)`); load(); }
    catch { toast.error("Could not clear events"); }
  };

  if (!data) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#22d3ee]" size={28} /></div>;
  return (
    <div>
      <AdminHeader title="Reports & Analytics" subtitle="Internal event tracking — the foundation for future organizer analytics." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {data.events_by_type.map((e) => { const Icon = ICONS[e.event_type] || Activity; return (
          <div key={e.event_type} className="bg-white border border-slate-200 rounded-xl p-5"><Icon size={20} className="text-[#06b6d4]" /><div className="font-display font-black text-2xl text-slate-800 mt-2">{e.count.toLocaleString()}</div><div className="text-sm text-slate-500">{LABELS[e.event_type] || e.event_type}</div></div>
        ); })}
        {data.events_by_type.length === 0 && <p className="text-slate-500 col-span-4 bg-white border border-slate-200 rounded-xl p-8 text-center">No analytics events recorded yet.</p>}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-6 mt-6" data-testid="recent-events-panel">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold uppercase text-lg text-slate-800">Recent Events</h3>
          {data.recent.length > 0 && <button onClick={clearAll} className="text-xs font-semibold text-slate-500 hover:text-red-500 inline-flex items-center gap-1" data-testid="clear-recent-events"><Trash2 size={13} /> Clear All</button>}
        </div>
        <div className="space-y-2">{data.recent.map((r) => { const Icon = ICONS[r.event_type] || Activity; return (
          <div key={r.id} className="flex items-center gap-3 text-sm border-b border-slate-100 pb-2 group" data-testid={`recent-event-${r.id}`}>
            <Icon size={15} className="text-slate-400" />
            <span className="text-slate-700 font-medium">{LABELS[r.event_type] || r.event_type}</span>
            {r.metadata && Object.keys(r.metadata).length > 0 && <span className="text-slate-400 text-xs truncate max-w-[200px]">{JSON.stringify(r.metadata)}</span>}
            <span className="ml-auto text-slate-400 text-xs">{timeAgo(r.created_at)}</span>
            <button onClick={() => hide(r.id)} className="p-1 text-slate-300 hover:text-red-500 transition-colors" title="Remove from list" data-testid={`delete-recent-event-${r.id}`}><Trash2 size={14} /></button>
          </div>
        ); })}
        {data.recent.length === 0 && <p className="text-slate-400 text-sm py-4 text-center">No recent events to show.</p>}
        </div>
      </div>
    </div>
  );
}
